import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  startTransition,
} from 'react';
import {
  DashboardCard,
  StatsGrid,
  StatItem,
  SessionList,
  Session,
  SessionListActivity,
  ActionItem,
  BottomSheet,
} from '../../../shared/ui';
import BottomSelectionPanel from '../../../shared/ui/BottomSelectionPanel';
import StatusActionButton from '../../../shared/ui/buttons/StatusActionButton';
import { useAllSitesShifts } from '../hooks/useAllSitesShifts';
import {
  SessionsV2Filters,
  SessionV2,
  SessionActivity,
  SessionChangeHistoryEvent,
  sessionsV2Api,
} from '../../../api/sessionsV2Api';
import { paymentsV2Api } from '../../../api/paymentsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useToast } from '../../../shared/contexts/ToastContext';
import AllSitesShiftsFilter from './AllSitesShiftsFilter';
import EditSessionV2Modal from './EditSessionV2Modal';
import ActivityPaymentsBottomSheet from './ActivityPaymentsBottomSheet';
import PaymentSheetHost from './PaymentSheetHost';
import BonusSheetHost from './BonusSheetHost';
import { getMediaUrl } from '../../../utils/avatarUtils';
import ImageViewer from '../../profile/ui/ImageViewer';
import styles from './SiteShiftsCard.module.css';

const AllSitesShiftsCard: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [filters, setFilters] = useState<SessionsV2Filters>({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenuBar, setShowActionsMenuBar] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Set<number>>(
    new Set()
  );
  const paymentSheetHostRef = useRef<{ open: (sessions: SessionV2[]) => void }>(
    null
  );
  const bonusSheetHostRef = useRef<{ open: (sessions: SessionV2[]) => void }>(
    null
  );
  const [paymentSiteId, setPaymentSiteId] = useState(0);
  const [selectedSession, setSelectedSession] = useState<SessionV2 | null>(null);
  const [selectedActivity, setSelectedActivity] =
    useState<SessionListActivity | null>(null);
  const [selectedActivitySiteId, setSelectedActivitySiteId] = useState<
    number | null
  >(null);
  const [showAdditionalWorkReasonSheet, setShowAdditionalWorkReasonSheet] =
    useState(false);
  const [additionalWorkReason, setAdditionalWorkReason] = useState<
    string | null
  >(null);
  const [additionalWorkReasonLoading, setAdditionalWorkReasonLoading] =
    useState(false);
  const [additionalWorkReasonActivity, setAdditionalWorkReasonActivity] =
    useState<{
      activityId: number;
      sessionId: number;
      typeOfWorkName?: string;
    } | null>(null);
  const [additionalWorkReasonPhotos, setAdditionalWorkReasonPhotos] = useState<
    Array<{ id: number; sizes: Array<{ sizeType: string; url: string }> }>
  >([]);
  const [additionalWorkPhotoViewerOpen, setAdditionalWorkPhotoViewerOpen] =
    useState(false);
  const [additionalWorkPhotoViewerIndex, setAdditionalWorkPhotoViewerIndex] =
    useState(0);
  const [showCommentInfoSheet, setShowCommentInfoSheet] = useState(false);
  const [commentInfoActivity, setCommentInfoActivity] = useState<{
    activityId: number;
    sessionId: number;
    typeOfWorkName?: string;
    comment?: string;
  } | null>(null);
  const [commentInfoPhotos, setCommentInfoPhotos] = useState<
    Array<{ id: number; sizes: Array<{ sizeType: string; url: string }> }>
  >([]);
  const [commentInfoLoading, setCommentInfoLoading] = useState(false);
  const [commentPhotoViewerOpen, setCommentPhotoViewerOpen] = useState(false);
  const [commentPhotoViewerIndex, setCommentPhotoViewerIndex] = useState(0);
  const [sessionActivities, setSessionActivities] = useState<
    Record<number, SessionActivity[]>
  >({});
  const [loadingActivities, setLoadingActivities] = useState<
    Record<number, boolean>
  >({});
  const [allFinancialVisible, setAllFinancialVisible] = useState(false);
  const [uncensoredSessionIds, setUncensoredSessionIds] = useState<Set<number>>(
    new Set()
  );
  const [showDeleteConfirmSheet, setShowDeleteConfirmSheet] = useState(false);
  const [sessionsToDelete, setSessionsToDelete] = useState<SessionV2[]>([]);
  const [showSessionHistorySheet, setShowSessionHistorySheet] = useState(false);
  const [sessionForHistory, setSessionForHistory] = useState<SessionV2 | null>(
    null
  );
  const [sessionHistoryEvents, setSessionHistoryEvents] = useState<
    SessionChangeHistoryEvent[]
  >([]);
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(false);
  const [sessionHistoryError, setSessionHistoryError] = useState<string | null>(
    null
  );
  const [unallocatedAdvancesCache, setUnallocatedAdvancesCache] = useState<
    Record<number, number>
  >({});
  const [unallocatedAdvancesLoadingUserId, setUnallocatedAdvancesLoadingUserId] =
    useState<number | null>(null);
  const loadedSessionsRef = useRef<Set<number>>(new Set());
  const lastTypeOfWorkIdRef = useRef<number | undefined>(undefined);
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const sessionsByIdRef = useRef<Map<number, SessionV2>>(new Map());

  const { sessions, loading, error, statistics, refresh } = useAllSitesShifts({
    filters,
    enabled: !isCollapsed || isFullscreenOpen,
  });

  sessionsByIdRef.current = useMemo(() => {
    const m = new Map<number, SessionV2>();
    sessions.forEach((s) => m.set(s.id, s));
    return m;
  }, [sessions]);

  const handleFiltersChange = (newFilters: SessionsV2Filters) => {
    if (newFilters.typeOfWorkId !== filters.typeOfWorkId) {
      setSessionActivities({});
      loadedSessionsRef.current.clear();
    }
    setFilters(newFilters);
  };

  const handleSessionEdited = () => {
    setShowEditModal(false);
    setSelectedSession(null);
    setShowActionsMenuBar(false);
    setSelectedSessions(new Set());
    setSessionActivities({});
    loadedSessionsRef.current.clear();
    refresh();
  };

  const handleSessionSelect = useCallback(
    (session: Session | SessionV2) => {
      if (!session || !user) return;
      setSelectedSessions((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(session.id)) {
          newSet.delete(session.id);
        } else {
          newSet.add(session.id);
        }
        setShowActionsMenuBar(newSet.size > 0);
        return newSet;
      });
    },
    [user]
  );

  const handleDeleteClick = useCallback(() => {
    if (!user || selectedSessions.size === 0) return;
    const list = sessions.filter((s) => selectedSessions.has(s.id));
    setSessionsToDelete(list);
    setShowDeleteConfirmSheet(true);
  }, [user, selectedSessions, sessions]);

  const performDelete = useCallback(
    async (list: SessionV2[]) => {
      if (list.length === 0) return;
      try {
        await Promise.all(
          list.map((session) =>
            sessionsV2Api.deleteSession(session.siteId, session.id)
          )
        );
        const count = list.length;
        showSuccess(
          count === 1 ? 'Смена успешно удалена' : `${count} смен успешно удалено`
        );
        setSelectedSessions(new Set());
        setShowActionsMenuBar(false);
        setShowDeleteConfirmSheet(false);
        setSessionsToDelete([]);
        refresh();
      } catch (err: any) {
        console.error('Error deleting sessions:', err);
        showError(
          err.response?.data?.message || 'Ошибка удаления смен'
        );
      }
    },
    [showSuccess, showError, refresh]
  );

  const handleActivityPaymentsClick = useCallback(
    (activity: SessionListActivity, sessionId: number) => {
      const session = sessionsByIdRef.current.get(sessionId);
      setSelectedActivity(null);
      setSelectedActivitySiteId(session?.siteId ?? null);
      setShowActivityPaymentsSheet(true);
      queueMicrotask(() => setSelectedActivity(activity));
    },
    []
  );

  const handleAdditionalWorkReasonClick = useCallback(
    async (
      activity: { id: number; typeOfWorkName?: string },
      sessionId: number
    ) => {
      const session = sessionsByIdRef.current.get(sessionId);
      const siteId = session?.siteId;
      if (!siteId) return;
      setAdditionalWorkReasonActivity({
        activityId: activity.id,
        sessionId,
        typeOfWorkName: activity.typeOfWorkName,
      });
      setShowAdditionalWorkReasonSheet(true);
      setAdditionalWorkReason(null);
      setAdditionalWorkReasonPhotos([]);
      setAdditionalWorkReasonLoading(true);
      try {
        const [history, photos] = await Promise.all([
          sessionsV2Api.getActivityAdditionalWorkHistory(
            siteId,
            sessionId,
            activity.id
          ),
          sessionsV2Api
            .getAdditionalWorkPhotos(siteId, sessionId, activity.id)
            .catch(() => []),
        ]);
        const activeRecord = history.find((h: { revokedAt?: string }) => !h.revokedAt);
        setAdditionalWorkReason(activeRecord?.addedReason?.trim() || null);
        setAdditionalWorkReasonPhotos(photos);
      } catch (err) {
        console.error('Error fetching additional work history:', err);
        setAdditionalWorkReason(null);
        showError('Не удалось загрузить причину');
      } finally {
        setAdditionalWorkReasonLoading(false);
      }
    },
    [showError]
  );

  const handleCommentClick = useCallback(
    async (
      activity: { id: number; typeOfWorkName?: string; comment?: string },
      sessionId: number
    ) => {
      const session = sessionsByIdRef.current.get(sessionId);
      const siteId = session?.siteId;
      if (!siteId) return;
      setCommentInfoActivity({
        activityId: activity.id,
        sessionId,
        typeOfWorkName: activity.typeOfWorkName,
        comment: activity.comment,
      });
      setShowCommentInfoSheet(true);
      setCommentInfoPhotos([]);
      setCommentInfoLoading(true);
      try {
        const photos = await sessionsV2Api
          .getActivityCommentPhotos(siteId, sessionId, activity.id)
          .catch(() => []);
        setCommentInfoPhotos(photos);
      } catch (err) {
        console.error('Error fetching comment photos:', err);
      } finally {
        setCommentInfoLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!showSessionHistorySheet || !sessionForHistory) {
      setSessionHistoryEvents([]);
      setSessionHistoryError(null);
      return;
    }
    let cancelled = false;
    setSessionHistoryLoading(true);
    setSessionHistoryError(null);
    sessionsV2Api
      .getSessionChangeHistory(sessionForHistory.siteId, sessionForHistory.id)
      .then((data) => {
        if (!cancelled) {
          setSessionHistoryEvents(data);
          setSessionHistoryError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSessionHistoryEvents([]);
          setSessionHistoryError(
            err?.response?.data || 'Не удалось загрузить историю изменений'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSessionHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showSessionHistorySheet, sessionForHistory]);

  useEffect(() => {
    if (lastTypeOfWorkIdRef.current !== filters.typeOfWorkId) {
      setSessionActivities({});
      loadedSessionsRef.current.clear();
      lastTypeOfWorkIdRef.current = filters.typeOfWorkId;
    }

    const currentSessionIds = new Set(sessions.map((s) => s.id));
    const loadedIds = Array.from(loadedSessionsRef.current);
    loadedIds.forEach((id) => {
      if (!currentSessionIds.has(id)) {
        loadedSessionsRef.current.delete(id);
        setSessionActivities((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    });

    const loadAllActivities = async () => {
      const bySite = new Map<number, number[]>();
      sessions.forEach((s) => {
        if (
          !loadedSessionsRef.current.has(s.id) &&
          !loadingActivities[s.id]
        ) {
          const list = bySite.get(s.siteId) || [];
          list.push(s.id);
          bySite.set(s.siteId, list);
        }
      });

      const allSessionIds = Array.from(bySite.values()).flat();
      if (allSessionIds.length === 0) return;

      allSessionIds.forEach((id) => loadedSessionsRef.current.add(id));

      try {
        const promises = Array.from(bySite.entries()).map(
          async ([siteId, sessionIds]) => {
            try {
              const activitiesMap =
                await sessionsV2Api.getBatchSessionActivities(
                  siteId,
                  sessionIds
                );
              sessionIds.forEach((sessionId) => {
                let activities = activitiesMap.get(sessionId) || [];
                if (
                  filters.typeOfWorkId !== undefined &&
                  filters.typeOfWorkId !== 0
                ) {
                  activities = activities.filter(
                    (a) => a.typeOfWorkId === filters.typeOfWorkId
                  );
                }
                setSessionActivities((prev) => ({
                  ...prev,
                  [sessionId]: activities,
                }));
              });
            } catch (e) {
              sessionIds.forEach((sessionId) =>
                setSessionActivities((prev) => ({ ...prev, [sessionId]: [] }))
              );
            }
          }
        );
        await Promise.all(promises);
      } catch (error) {
        console.error('Error loading activities:', error);
        allSessionIds.forEach((sessionId) =>
          setSessionActivities((prev) => ({ ...prev, [sessionId]: [] }))
        );
      } finally {
        setLoadingActivities((prev) => {
          const next = { ...prev };
          allSessionIds.forEach((id) => delete next[id]);
          return next;
        });
      }
    };

    loadAllActivities();
  }, [sessions, filters.typeOfWorkId]);

  const handleEdit = useCallback(
    (session: SessionV2) => {
      if (!session || !user) return;
      setShowActionsMenuBar(false);
      setSelectedSessions(new Set());
      setSelectedSession(session);
      setShowEditModal(true);
      sessionsV2Api
        .checkEditPermission(session.siteId, session.id)
        .then((result) => {
          if (!result.canEdit) {
            showError('У вас нет прав на редактирование этой смены');
            setShowEditModal(false);
            setSelectedSession(null);
          }
        })
        .catch(() => {
          showError('Ошибка при проверке прав доступа');
          setShowEditModal(false);
          setSelectedSession(null);
        });
    },
    [user, showError]
  );

  const handleUnsetWorkTypeClick = useCallback(
    (sessionId: number) => {
      const session = sessions.find((s) => s.id === sessionId);
      if (session) handleEdit(session);
    },
    [sessions, handleEdit]
  );

  const handleCloseActionsMenuBar = useCallback(() => {
    setShowActionsMenuBar(false);
    setSelectedSessions(new Set());
  }, []);

  const handleCancelSelection = useCallback(() => {
    if (selectedSessions.size === 0) return;
    startTransition(() => {
      setSelectedSessions(new Set());
    });
  }, [selectedSessions]);

  const requestUnallocatedAdvances = useCallback(
    (userId: number, siteId: number) => {
      setUnallocatedAdvancesCache((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setUnallocatedAdvancesLoadingUserId(userId);
      paymentsV2Api
        .getSitePaymentsWithRemaining(siteId, { userId })
        .then((payments) => {
          const sum = payments.reduce(
            (acc, p) => acc + (p.remainingAmount ?? 0),
            0
          );
          setUnallocatedAdvancesCache((prev) => ({
            ...prev,
            [userId]: Math.round(sum * 100) / 100,
          }));
        })
        .catch((err) => {
          console.error('Error loading unallocated advances:', err);
          showError('Не удалось загрузить сумму нераспределённых авансов');
        })
        .finally(() => setUnallocatedAdvancesLoadingUserId(null));
    },
    [showError]
  );

  const selectedSessionsList = useMemo(
    () => sessions.filter((s) => selectedSessions.has(s.id)),
    [sessions, selectedSessions]
  );
  const selectedUserId =
    selectedSessionsList.length > 0 &&
    new Set(selectedSessionsList.map((s) => s.userId)).size === 1
      ? selectedSessionsList[0].userId
      : null;
  const selectedSiteId =
    selectedSessionsList.length > 0 &&
    new Set(selectedSessionsList.map((s) => s.siteId)).size === 1
      ? selectedSessionsList[0].siteId
      : null;

  useEffect(() => {
    if (selectedSessions.size === 0 || !selectedUserId || !selectedSiteId) return;
    requestUnallocatedAdvances(selectedUserId, selectedSiteId);
  }, [selectedSessions, selectedUserId, selectedSiteId, requestUnallocatedAdvances]);

  const selectedSessionsSummary = useMemo(() => {
    if (selectedSessions.size === 0) return undefined;
    const list = selectedSessionsList;
    let totalAmount = 0;
    let paidAmount = 0;
    let remainingAmount = 0;
    let totalHours = 0;
    let hasFinancialAccess = false;
    list.forEach((session) => {
      const activities = sessionActivities[session.id] || [];
      if (activities.length > 0) {
        activities.forEach((a) => {
          if (a.hasFinancialAccess) {
            totalAmount += a.amount ?? 0;
            paidAmount += a.paidAmount ?? 0;
            remainingAmount += a.remainingAmount ?? 0;
            hasFinancialAccess = true;
          }
        });
      } else if (session.hasFinancialAccess) {
        totalAmount += session.totalAmount ?? 0;
        paidAmount += session.paidAmount ?? 0;
        remainingAmount += session.remainingAmount ?? 0;
        hasFinancialAccess = true;
      }
      totalHours += session.hours ?? 0;
    });
    return {
      totalAmount,
      paidAmount,
      remainingAmount,
      totalHours,
      hasFinancialAccess,
      uniqueUsers: new Set(list.map((s) => s.userId)).size,
      unallocatedAdvancesSum:
        selectedUserId != null &&
        unallocatedAdvancesCache[selectedUserId] !== undefined
          ? unallocatedAdvancesCache[selectedUserId]
          : undefined,
      unallocatedAdvancesLoading:
        selectedUserId != null &&
        unallocatedAdvancesLoadingUserId === selectedUserId,
    };
  }, [
    selectedSessions,
    selectedSessionsList,
    sessionActivities,
    selectedUserId,
    unallocatedAdvancesCache,
    unallocatedAdvancesLoadingUserId,
  ]);

  const isSessionPaid = useCallback(
    (session: SessionV2, activities: SessionActivity[]): boolean => {
      if (!session.hasFinancialAccess) return false;
      if (activities.length > 0) {
        let totalAmount = 0;
        let paidAmount = 0;
        let remainingAmount = 0;
        activities.forEach((a) => {
          if (a.hasFinancialAccess) {
            totalAmount += a.amount ?? 0;
            paidAmount += a.paidAmount ?? 0;
            remainingAmount += a.remainingAmount ?? 0;
          }
        });
        const nr = Math.round(remainingAmount * 100) / 100;
        const np = Math.round(paidAmount * 100) / 100;
        const nt = Math.round(totalAmount * 100) / 100;
        return (
          nr <= 0.01 ||
          np >= nt ||
          (np > 0 && nt > 0 && Math.abs(np - nt) < 0.01)
        );
      }
      const r = session.remainingAmount ?? 0;
      const p = session.paidAmount ?? 0;
      const t = session.totalAmount ?? 0;
      const nr = Math.round(r * 100) / 100;
      const np = Math.round(p * 100) / 100;
      const nt = Math.round(t * 100) / 100;
      return (
        nr <= 0.01 ||
        np >= nt ||
        (np > 0 && nt > 0 && Math.abs(np - nt) < 0.01)
      );
    },
    []
  );

  const sessionListData = useMemo(() => {
    let filtered = sessions;
    if (filters.typeOfWorkId === 0) {
      filtered = filtered.filter((session) => {
        const activities = sessionActivities[session.id] || [];
        return activities.some(
          (a) => !a.typeOfWorkId || a.typeOfWorkId <= 1
        );
      });
    }
    if (filters.paymentStatus) {
      filtered = filtered.filter((session) => {
        const activities = sessionActivities[session.id] || [];
        const isPaid = isSessionPaid(session, activities);
        if (filters.paymentStatus === 'paid') return isPaid;
        if (filters.paymentStatus === 'unpaid') return !isPaid;
        return true;
      });
    }
    if (filters.isAdditionalWork !== undefined) {
      filtered = filtered.filter((session) => {
        const activities = sessionActivities[session.id] || [];
        const has = activities.some((a) => a.isAdditionalWork === true);
        return filters.isAdditionalWork ? has : !has;
      });
    }

    return filtered.map((session) => {
      let activities = sessionActivities[session.id] || [];
      if (filters.isAdditionalWork === true) {
        activities = activities.filter((a) => a.isAdditionalWork === true);
      } else if (filters.isAdditionalWork === false) {
        activities = activities.filter((a) => a.isAdditionalWork !== true);
      }
      return {
        id: session.id,
        userId: session.userId,
        userName: session.userName,
        siteName: session.siteName,
        timeStart: session.timeStart,
        timeEnd: session.timeEnd,
        hours: session.hours,
        totalAmount: session.totalAmount,
        paidAmount: session.paidAmount,
        compensationAmount: session.compensationAmount,
        compensationPaidAmount: session.compensationPaidAmount,
        remainingAmount: session.remainingAmount,
        hasFinancialAccess: session.hasFinancialAccess,
        activities: activities.map((a) => ({
          id: a.id,
          typeOfWorkId: a.typeOfWorkId,
          typeOfWorkName: a.typeOfWorkName,
          timeStart: a.timeStart,
          timeEnd: a.timeEnd,
          hours: a.hours,
          allocatedHours: a.allocatedHours,
          sortOrder: a.sortOrder,
          amount: a.amount,
          paidAmount: a.paidAmount,
          bonusPaidAmount: a.bonusPaidAmount,
          remainingAmount: a.remainingAmount,
          rate: a.rate,
          hasFinancialAccess: a.hasFinancialAccess,
          comment: a.comment,
          isAdditionalWork: a.isAdditionalWork,
        })),
      } as Session;
    });
  }, [
    sessions,
    sessionActivities,
    filters.typeOfWorkId,
    filters.paymentStatus,
    filters.isAdditionalWork,
    isSessionPaid,
  ]);

  const filteredStatistics = useMemo(() => {
    if (
      filters.typeOfWorkId === undefined &&
      filters.isAdditionalWork === undefined
    ) {
      return statistics;
    }
    let totalHours = 0;
    let totalAmount = 0;
    let paidAmount = 0;
    let bonusPaidAmount = 0;
    let remainingAmount = 0;
    let hasFinancialAccess = false;
    const sessionIdsWithActivities = new Set<number>();

    Object.entries(sessionActivities).forEach(([sessionIdStr, activities]) => {
      const session = sessionsByIdRef.current.get(Number(sessionIdStr));
      if (!session) return;
      if (
        filters.typeOfWorkId === 0 &&
        !activities.some((a) => !a.typeOfWorkId || a.typeOfWorkId <= 1)
      )
        return;
      let filteredActivities = activities;
      if (filters.isAdditionalWork === true) {
        filteredActivities = activities.filter((a) => a.isAdditionalWork === true);
      } else if (filters.isAdditionalWork === false) {
        filteredActivities = activities.filter((a) => a.isAdditionalWork !== true);
      }
      if (
        filters.typeOfWorkId !== undefined &&
        filters.typeOfWorkId !== 0
      ) {
        filteredActivities = filteredActivities.filter(
          (a) => a.typeOfWorkId === filters.typeOfWorkId
        );
      }
      if (filteredActivities.length > 0) {
        sessionIdsWithActivities.add(Number(sessionIdStr));
        filteredActivities.forEach((a) => {
          if (a.hours) totalHours += a.hours;
          if (a.amount) totalAmount += a.amount;
          if (a.paidAmount) paidAmount += a.paidAmount;
          if (a.bonusPaidAmount) bonusPaidAmount += a.bonusPaidAmount;
          if (a.remainingAmount) remainingAmount += a.remainingAmount;
          if (a.hasFinancialAccess) hasFinancialAccess = true;
        });
      }
    });

    return {
      totalSessions: sessionIdsWithActivities.size,
      totalHours,
      totalAmount,
      paidAmount,
      bonusPaidAmount,
      remainingAmount,
      hasFinancialAccess,
      uniqueUsers: new Set(
        sessions
          .filter((s) => sessionIdsWithActivities.has(s.id))
          .map((s) => s.userId)
      ).size,
    };
  }, [sessionActivities, filters.typeOfWorkId, filters.isAdditionalWork, sessions, statistics]);

  const paidTotal =
    (filteredStatistics.paidAmount ?? 0) +
    (filteredStatistics.bonusPaidAmount ?? 0);
  const statsData: StatItem[] = [
    {
      id: 'sessions',
      value: filteredStatistics.totalSessions,
      label: 'Смен',
      icon: 'ri-calendar-line',
      color: 'primary',
    },
    {
      id: 'hours',
      value: Math.round(filteredStatistics.totalHours * 10) / 10,
      label: 'Часов',
      icon: 'ri-time-line',
      color: 'muted',
    },
    {
      id: 'earned',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(filteredStatistics.totalAmount).toLocaleString('ru-RU')
        : '—',
      label: 'Заработано',
      icon: 'ri-coins-line',
      color: 'success',
    },
    {
      id: 'remaining',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(filteredStatistics.remainingAmount ?? 0).toLocaleString(
            'ru-RU'
          )
        : '—',
      label: 'Остаток',
      icon: 'ri-time-line',
      color: 'warning',
    },
  ];

  const sessionActions: ActionItem[] = useMemo(() => {
    if (selectedSessions.size === 0) return [];
    const list = selectedSessionsList;
    const isSingle = selectedSessions.size === 1;
    const singleSession = isSingle ? list[0] : null;
    const actions: ActionItem[] = [];

    if (isSingle && singleSession) {
      actions.push({
        id: 'edit',
        label: 'Редактировать',
        icon: 'ri-edit-line',
        onClick: () => handleEdit(singleSession),
        variant: 'primary',
      });
      actions.push({
        id: 'history',
        label: 'История изменений',
        icon: 'ri-history-line',
        onClick: () => {
          setSessionForHistory(singleSession);
          setShowSessionHistorySheet(true);
          queueMicrotask(() => setShowActionsMenuBar(false));
        },
        variant: 'secondary',
      });
    }

    const uniqueUserIds = new Set(list.map((s) => s.userId));
    const uniqueSiteIds = new Set(list.map((s) => s.siteId));
    const isSameUser = uniqueUserIds.size === 1;
    const isSameSite = uniqueSiteIds.size === 1;

    if (isSameUser && isSameSite) {
      actions.push({
        id: 'pay',
        label: 'Оплатить',
        icon: 'ri-money-dollar-circle-line',
        onClick: () => {
          setPaymentSiteId(list[0].siteId);
          requestAnimationFrame(() => {
            paymentSheetHostRef.current?.open(list);
            queueMicrotask(() => setShowActionsMenuBar(false));
          });
        },
        variant: 'success',
      });
      actions.push({
        id: 'bonus',
        label: 'Премировать',
        icon: 'ri-gift-line',
        onClick: () => {
          setPaymentSiteId(list[0].siteId);
          requestAnimationFrame(() => {
            bonusSheetHostRef.current?.open(list);
            queueMicrotask(() => setShowActionsMenuBar(false));
          });
        },
        variant: 'primary',
      });
    }

    actions.push({
      id: 'delete',
      label: isSingle ? 'Удалить' : `Удалить (${selectedSessions.size})`,
      icon: 'ri-delete-bin-line',
      onClick: handleDeleteClick,
      variant: 'danger',
    });

    return actions;
  }, [selectedSessions, selectedSessionsList, handleDeleteClick, handleEdit]);

  const handleClosePayment = useCallback(() => {
    setSelectedSessions(new Set());
  }, []);

  const handleSuccessPayment = useCallback(() => {
    setSelectedSessions(new Set());
    setUnallocatedAdvancesCache({});
    refresh();
  }, [refresh]);

  const formatTime = (timeString: string) =>
    new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatDate = (timeString: string) => {
    const date = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' });
    if (date.toDateString() === today.toDateString())
      return `Сегодня, ${weekday}`;
    if (date.toDateString() === yesterday.toDateString())
      return `Вчера, ${weekday}`;
    return `${date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}, ${weekday}`;
  };

  const formatDuration = (hours?: number) => {
    if (!hours) return { hours: '0', minutes: null };
    const wholeHours = Math.floor(hours);
    let minutes = Math.round((hours - wholeHours) * 60);
    if (minutes >= 60) {
      minutes = minutes % 60;
      const totalHours = wholeHours + Math.floor(minutes / 60);
      return { hours: `${totalHours}`, minutes: minutes === 0 ? null : `${minutes}` };
    }
    return { hours: `${wholeHours}`, minutes: minutes === 0 ? null : `${minutes}` };
  };

  const formatDurationString = (hours?: number) => {
    if (!hours) return '0 ч';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h} ч`;
    return `${h} ч ${m} м`;
  };

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
  };

  const formatHistoryValue = (fieldName: string, value: string): string => {
    if (fieldName === 'TimeStart' || fieldName === 'TimeEnd') {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime()))
        return d.toLocaleString('ru-RU', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
    }
    if (fieldName === 'AllocatedHours') {
      const n = parseFloat(value);
      if (!Number.isNaN(n)) return `${n} ч`;
    }
    if (fieldName === 'Rate') {
      const n = parseFloat(value);
      if (!Number.isNaN(n)) return n.toFixed(2);
    }
    return value;
  };

  const isFinancialVisible = useCallback(
    (sessionId: number) =>
      allFinancialVisible || uncensoredSessionIds.has(sessionId),
    [allFinancialVisible, uncensoredSessionIds]
  );

  const handleToggleFinancialVisible = useCallback((sessionId: number) => {
    setUncensoredSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  }, []);

  const handleToggleAllFinancialVisible = useCallback(() => {
    setAllFinancialVisible((prev) => {
      if (prev) setUncensoredSessionIds(new Set());
      return !prev;
    });
  }, []);

  const [showActivityPaymentsSheet, setShowActivityPaymentsSheet] =
    useState(false);

  return (
    <DashboardCard
      id="all-sites-shifts-card"
      title="Общие смены"
      icon="ri-calendar-line"
      isCollapsible
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      onFullscreenOpenChange={setIsFullscreenOpen}
      headerActions={
        <button
          className="card-action-btn"
          onClick={() => {
            setSessionActivities({});
            loadedSessionsRef.current.clear();
            refresh();
          }}
          disabled={loading}
          title="Обновить данные"
        >
          <i
            className={
              loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'
            }
          />
        </button>
      }
    >
      <StatsGrid items={statsData} />
      <AllSitesShiftsFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
        loading={false}
      />

      {!error && sessionListData.length > 0 && statistics.hasFinancialAccess && (
        <div className={styles.selectAllSection}>
          <div className={styles.selectAllRow}>
            <button
              type="button"
              className={styles.financialCensorGlobalEye}
              onClick={handleToggleAllFinancialVisible}
              title={
                allFinancialVisible
                  ? 'Скрыть финансы по всем сменам'
                  : 'Показать финансы по всем сменам'
              }
              aria-label={allFinancialVisible ? 'Скрыть финансы' : 'Показать финансы'}
            >
              <i
                className={
                  allFinancialVisible ? 'ri-eye-off-line' : 'ri-eye-line'
                }
              />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorState}>
          <i className="ri-error-warning-line" />
          <span>{error}</span>
          <button className={styles.retryButton} onClick={refresh}>
            <i className="ri-refresh-line" />
            Повторить
          </button>
        </div>
      )}

      {!error && (
        <SessionList
          sessions={sessionListData}
          onSessionSelect={handleSessionSelect}
          selectedSessionIds={selectedSessions}
          onActivityPaymentsClick={handleActivityPaymentsClick}
          onAdditionalWorkReasonClick={handleAdditionalWorkReasonClick}
          onCommentClick={handleCommentClick}
          onUnsetWorkTypeClick={handleUnsetWorkTypeClick}
          isFinancialVisible={
            statistics.hasFinancialAccess ? isFinancialVisible : undefined
          }
          onToggleFinancialVisible={
            statistics.hasFinancialAccess ? handleToggleFinancialVisible : undefined
          }
          formatTime={formatTime}
          formatDate={formatDate}
          formatDuration={formatDuration}
          formatAmount={formatAmount}
          loading={loading}
          loadingActivities={loadingActivities}
          emptyState={{
            icon: 'ri-calendar-line',
            title: 'Смены не найдены',
            subtitle: 'Попробуйте изменить фильтры',
          }}
        />
      )}

      {!loading && !error && sessions.length > 0 && !statistics.hasFinancialAccess && (
        <div className={styles.permissionsInfo}>
          <i className="ri-information-line" />
          <span>
            Финансовая информация скрыта. Обратитесь к администратору для получения доступа.
          </span>
        </div>
      )}

      <EditSessionV2Modal
        isOpen={showEditModal}
        session={selectedSession}
        siteId={selectedSession?.siteId ?? 0}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSession(null);
        }}
        onSuccess={handleSessionEdited}
      />

      <BottomSelectionPanel
        isOpen={showActionsMenuBar || selectedSessions.size > 0}
        actions={sessionActions}
        onClose={handleCloseActionsMenuBar}
        onCancel={handleCancelSelection}
        selectionSummary={selectedSessionsSummary}
        formatAmount={formatAmount}
        formatHours={formatDurationString}
      />

      <ActivityPaymentsBottomSheet
        isOpen={showActivityPaymentsSheet}
        activity={selectedActivity}
        siteId={selectedActivitySiteId ?? 0}
        onClose={() => {
          setShowActivityPaymentsSheet(false);
          setSelectedActivity(null);
          setSelectedActivitySiteId(null);
        }}
      />

      <BottomSheet
        isOpen={showDeleteConfirmSheet}
        onClose={() => {
          setShowDeleteConfirmSheet(false);
          setSessionsToDelete([]);
        }}
        title={
          sessionsToDelete.length === 1
            ? 'Удалить смену?'
            : `Удалить смены (${sessionsToDelete.length})?`
        }
        titleClassName={styles.deleteConfirmSheetTitle}
        size={sessionsToDelete.length <= 2 ? 'small' : 'medium'}
        showHandle
        allowSwipeToClose
      >
        <div className={styles.deleteConfirmContent}>
          <div className={styles.deleteConfirmDetails}>
            {sessionsToDelete.slice(0, 5).map((session) => {
              const activities = sessionActivities[session.id] ?? [];
              const hoursStr = formatDurationString(session.hours);
              const dateStr = formatDate(session.timeStart);
              const timeStr = session.timeEnd
                ? `${formatTime(session.timeStart)} — ${formatTime(session.timeEnd)}`
                : formatTime(session.timeStart);
              const activityWord =
                activities.length === 1
                  ? 'активность'
                  : activities.length >= 2 && activities.length <= 4
                    ? 'активности'
                    : 'активностей';
              const activityNames = activities
                .slice(0, 3)
                .map((a) => a.typeOfWorkName || '—')
                .join(', ');
              const moreCount = activities.length > 3 ? activities.length - 3 : 0;
              const activitySuffix =
                activities.length > 0
                  ? ` · ${activities.length} ${activityWord}${activityNames ? ': ' + activityNames + (moreCount > 0 ? ' +' + moreCount : '') : ''}`
                  : null;
              return (
                <div
                  key={session.id}
                  className={styles.deleteConfirmSessionRow}
                >
                  <span className={styles.deleteConfirmSessionUser}>
                    {session.userName || `Пользователь ${session.userId}`}
                    {session.siteName && ` · ${session.siteName}`}
                  </span>
                  <span className={styles.deleteConfirmSessionMeta}>
                    {dateStr} · {timeStr} · {hoursStr}
                    {activitySuffix}
                  </span>
                </div>
              );
            })}
            {sessionsToDelete.length > 5 && (
              <div className={styles.deleteConfirmSessionMore}>
                и ещё {sessionsToDelete.length - 5} смен
              </div>
            )}
          </div>
          <div className={styles.deleteConfirmActions}>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={() => {
                setShowDeleteConfirmSheet(false);
                setSessionsToDelete([]);
              }}
              fullWidth
              icon="ri-close-line"
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="button"
              variant="danger"
              onClick={() => performDelete(sessionsToDelete)}
              fullWidth
              icon="ri-delete-bin-line"
            >
              Удалить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={showSessionHistorySheet}
        onClose={() => {
          setShowSessionHistorySheet(false);
          setSessionForHistory(null);
          setSessionHistoryEvents([]);
          setSessionHistoryError(null);
        }}
        title="История изменений"
        size="auto"
        showHandle
        allowSwipeToClose
      >
        <div className={styles.sessionHistoryContent}>
          {sessionForHistory && (
            <>
              <div className={styles.sessionHistorySummary}>
                <span className={styles.sessionHistoryUser}>
                  {sessionForHistory.userName ||
                    `Пользователь ${sessionForHistory.userId}`}
                  {sessionForHistory.siteName &&
                    ` · ${sessionForHistory.siteName}`}
                </span>
                <span className={styles.sessionHistoryMeta}>
                  {formatDate(sessionForHistory.timeStart)} ·{' '}
                  {formatTime(sessionForHistory.timeStart)}
                  {sessionForHistory.timeEnd
                    ? ` — ${formatTime(sessionForHistory.timeEnd)}`
                    : ''}
                </span>
              </div>
              {sessionHistoryLoading ? (
                <div className={styles.sessionHistoryLoading}>
                  <i className="ri-loader-4-line ri-spin" aria-hidden />
                  <span>Загрузка истории...</span>
                </div>
              ) : sessionHistoryError ? (
                <p className={styles.sessionHistoryError}>
                  {sessionHistoryError}
                </p>
              ) : sessionHistoryEvents.length === 0 ? (
                <p className={styles.sessionHistoryEmpty}>
                  Нет записей об изменениях
                </p>
              ) : (
                <ul className={styles.sessionHistoryList}>
                  {sessionHistoryEvents.map((ev, idx) => (
                    <li
                      key={`${ev.kind}-${ev.sessionActivityId ?? 's'}-${ev.occurredAt}-${idx}`}
                      className={styles.sessionHistoryItem}
                    >
                      <div className={styles.sessionHistoryItemHead}>
                        <span className={styles.sessionHistoryItemKind}>
                          {ev.kind === 'Session' ? 'Смена' : 'Активность'}
                          {ev.sessionActivityId != null &&
                            ` #${ev.sessionActivityId}`}
                        </span>
                        <span className={styles.sessionHistoryItemType}>
                          {ev.eventType === 'Created' && 'Создание'}
                          {ev.eventType === 'Updated' && 'Изменение'}
                          {ev.eventType === 'Deleted' && 'Удаление'}
                        </span>
                        <span className={styles.sessionHistoryItemTime}>
                          {new Date(ev.occurredAt).toLocaleString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {(ev.userName || ev.userId) && (
                          <span className={styles.sessionHistoryItemUser}>
                            {ev.userName || `Пользователь ${ev.userId}`}
                          </span>
                        )}
                      </div>
                      {ev.details && ev.details.length > 0 && (
                        <ul className={styles.sessionHistoryDetails}>
                          {ev.details.map((d, di) => {
                            const fieldLabel: Record<string, string> = {
                              TimeStart: 'Время начала',
                              TimeEnd: 'Время окончания',
                              Comment: 'Комментарий',
                              TypeOfWorkId: 'Вид работ',
                              Rate: 'Ставка',
                            };
                            const label =
                              fieldLabel[d.fieldName] || d.fieldName;
                            const oldVal =
                              d.oldValue != null && d.oldValue !== ''
                                ? formatHistoryValue(d.fieldName, d.oldValue)
                                : '—';
                            const newVal =
                              d.newValue != null && d.newValue !== ''
                                ? formatHistoryValue(d.fieldName, d.newValue)
                                : '—';
                            return (
                              <li
                                key={di}
                                className={styles.sessionHistoryDetailRow}
                              >
                                <span
                                  className={
                                    styles.sessionHistoryDetailField
                                  }
                                >
                                  {label}
                                </span>
                                {ev.eventType === 'Created' ? (
                                  <span
                                    className={
                                      styles.sessionHistoryDetailNew
                                    }
                                  >
                                    {newVal}
                                  </span>
                                ) : ev.eventType === 'Deleted' ? (
                                  <span
                                    className={
                                      styles.sessionHistoryDetailOld
                                    }
                                  >
                                    {oldVal}
                                  </span>
                                ) : (
                                  <span
                                    className={
                                      styles.sessionHistoryDetailChange
                                    }
                                  >
                                    {oldVal} → {newVal}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={showAdditionalWorkReasonSheet}
        onClose={() => {
          setShowAdditionalWorkReasonSheet(false);
          setAdditionalWorkReason(null);
          setAdditionalWorkReasonActivity(null);
          setAdditionalWorkReasonPhotos([]);
        }}
        title="Почему это доп.работа"
        size="auto"
      >
        <div className={styles.additionalWorkReasonContent}>
          {additionalWorkReasonActivity && (
            <div className={styles.additionalWorkReasonActivity}>
              {additionalWorkReasonActivity.typeOfWorkName || 'Вид работ не указан'}
            </div>
          )}
          {additionalWorkReasonLoading ? (
            <div className={styles.additionalWorkReasonLoading}>
              <i className="ri-loader-4-line ri-spin" />
              <span>Загрузка...</span>
            </div>
          ) : additionalWorkReason ? (
            <p className={styles.additionalWorkReasonText}>
              {additionalWorkReason}
            </p>
          ) : (
            <p className={styles.additionalWorkReasonEmpty}>
              Причина не указана
            </p>
          )}
          {additionalWorkReasonPhotos.length > 0 && (
            <div className={styles.additionalWorkReasonPhotos}>
              <div className={styles.additionalWorkReasonPhotosLabel}>
                Прикреплённые фото
              </div>
              <div className={styles.additionalWorkReasonPhotosGrid}>
                {additionalWorkReasonPhotos.map((media, index) => {
                  const url =
                    getMediaUrl(media as any, 'full') ||
                    getMediaUrl(media as any, 'avatar');
                  if (!url) return null;
                  return (
                    <button
                      key={media.id}
                      type="button"
                      className={styles.additionalWorkReasonPhotoItem}
                      onClick={() => {
                        setAdditionalWorkPhotoViewerIndex(index);
                        setAdditionalWorkPhotoViewerOpen(true);
                      }}
                    >
                      <img src={url} alt="" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </BottomSheet>

      {additionalWorkPhotoViewerOpen && additionalWorkReasonPhotos.length > 0 &&
        (() => {
          const photoUrls = additionalWorkReasonPhotos
            .map(
              (m) =>
                getMediaUrl(m as any, 'full') || getMediaUrl(m as any, 'avatar')
            )
            .filter((u): u is string => !!u);
          if (photoUrls.length === 0) return null;
          return (
            <ImageViewer
              images={photoUrls}
              initialIndex={Math.min(
                additionalWorkPhotoViewerIndex,
                photoUrls.length - 1
              )}
              onClose={() => setAdditionalWorkPhotoViewerOpen(false)}
              onIndexChange={setAdditionalWorkPhotoViewerIndex}
            />
          );
        })()}

      <BottomSheet
        isOpen={showCommentInfoSheet}
        onClose={() => {
          setShowCommentInfoSheet(false);
          setCommentInfoActivity(null);
          setCommentInfoPhotos([]);
        }}
        title="Комментарий к активности"
        size="auto"
      >
        <div className={styles.additionalWorkReasonContent}>
          {commentInfoActivity && (
            <div className={styles.additionalWorkReasonActivity}>
              {commentInfoActivity.typeOfWorkName || 'Вид работ не указан'}
            </div>
          )}
          {commentInfoActivity?.comment?.trim() ? (
            <p className={styles.additionalWorkReasonText}>
              {commentInfoActivity.comment}
            </p>
          ) : (
            <p className={styles.additionalWorkReasonEmpty}>
              Комментарий не указан
            </p>
          )}
          {!commentInfoLoading && commentInfoPhotos.length > 0 && (
            <div className={styles.additionalWorkReasonPhotos}>
              <div className={styles.additionalWorkReasonPhotosLabel}>
                Прикреплённые фото
              </div>
              <div className={styles.additionalWorkReasonPhotosGrid}>
                {commentInfoPhotos.map((media, index) => {
                  const url =
                    getMediaUrl(media as any, 'full') ||
                    getMediaUrl(media as any, 'avatar');
                  if (!url) return null;
                  return (
                    <button
                      key={media.id}
                      type="button"
                      className={styles.additionalWorkReasonPhotoItem}
                      onClick={() => {
                        setCommentPhotoViewerIndex(index);
                        setCommentPhotoViewerOpen(true);
                      }}
                    >
                      <img src={url} alt="" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </BottomSheet>

      {commentPhotoViewerOpen && commentInfoPhotos.length > 0 &&
        (() => {
          const photoUrls = commentInfoPhotos
            .map(
              (m) =>
                getMediaUrl(m as any, 'full') || getMediaUrl(m as any, 'avatar')
            )
            .filter((u): u is string => !!u);
          if (photoUrls.length === 0) return null;
          return (
            <ImageViewer
              images={photoUrls}
              initialIndex={Math.min(
                commentPhotoViewerIndex,
                photoUrls.length - 1
              )}
              onClose={() => setCommentPhotoViewerOpen(false)}
              onIndexChange={setCommentPhotoViewerIndex}
            />
          );
        })()}

      <PaymentSheetHost
        ref={paymentSheetHostRef}
        siteId={paymentSiteId}
        typeOfWorkId={filters.typeOfWorkId}
        isAdditionalWork={filters.isAdditionalWork}
        preloadedActivities={sessionActivities}
        onClose={handleClosePayment}
        onSuccess={handleSuccessPayment}
      />
      <BonusSheetHost
        ref={bonusSheetHostRef}
        siteId={paymentSiteId}
        onClose={handleClosePayment}
        onSuccess={handleSuccessPayment}
      />
    </DashboardCard>
  );
};

export default AllSitesShiftsCard;
